import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { formatSoles, cartItemPrice, fileUrl } from "@/api";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, clearCart, cartTotals } = useStore();
  const navigate = useNavigate();

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[var(--brand-background)] flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl flex items-center gap-2">
            <ShoppingBag size={22} /> Tu carrito
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-4 space-y-3">
          {cart.length === 0 && (
            <div className="text-center py-16 text-neutral-500">
              <p className="font-serif text-xl">Carrito vacío</p>
              <p className="text-sm mt-2">Agrega productos del catálogo</p>
            </div>
          )}
          {cart.map((it) => {
            const unit = cartItemPrice(it);
            const k = it.key || it.product_id;
            return (
              <div key={k} className="bg-white rounded-xl p-3 flex gap-3 border border-black/5">
                {it.image && (
                  <img src={fileUrl(it.image)} alt={it.name} className="h-20 w-20 object-cover rounded-lg" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">{it.code}</div>
                  <div className="font-semibold leading-tight truncate">{it.name}</div>
                  {(it.variant || it.color) && (
                    <div className="text-xs text-neutral-500">{[it.variant, it.color].filter(Boolean).join(" · ")}</div>
                  )}
                  <div className="mt-1 text-sm text-neutral-500">
                    {formatSoles(unit)} c/u
                    {unit < it.retail_price && (
                      <span className="ml-2 text-[var(--brand-accent)] font-semibold">Mayorista</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => updateQty(k, it.qty - 1)}
                        className="h-7 w-7 rounded-full bg-neutral-100 flex items-center justify-center"
                        data-testid={`cart-dec-${it.code}`}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-qty-${it.code}`}>{it.qty}</span>
                      <button
                        onClick={() => updateQty(k, it.qty + 1)}
                        className="h-7 w-7 rounded-full bg-neutral-100 flex items-center justify-center"
                        data-testid={`cart-inc-${it.code}`}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <button
                      onClick={() => removeFromCart(k)}
                      className="text-neutral-400 hover:text-[var(--brand-primary)]"
                      data-testid={`cart-remove-${it.code}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {cart.length > 0 && (
          <div className="border-t border-black/10 pt-4 space-y-3">
            <div className="flex items-center justify-between font-serif text-xl">
              <span>Total</span>
              <span className="font-bold text-[var(--brand-primary)]" data-testid="cart-total">{formatSoles(cartTotals.subtotal)}</span>
            </div>
            <Button
              className="btn-primary w-full h-12 rounded-full text-base font-semibold"
              onClick={() => {
                setCartOpen(false);
                navigate("/checkout");
              }}
              data-testid="cart-checkout-btn"
            >
              Ir al checkout
            </Button>
            <button
              onClick={clearCart}
              className="text-xs text-neutral-500 hover:text-[var(--brand-primary)] mx-auto block"
              data-testid="cart-clear-btn"
            >
              Vaciar carrito
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
